/**
 * FacilitatorPortal — standalone facilitator-facing portal.
 *
 * Migrated from two monolithic HTML prototypes (Facilitator_Dashboard.html
 * and Facilitator_Portal.html) into proper TSX modules.
 *
 * This renders OUTSIDE the admin Layout — it's a fully standalone app
 * with its own top bar (redesign screen 1, 1 Oct) and auth flow. Facilitators access it
 * directly at /facilitator-portal without touching the admin dashboard.
 */
import { Suspense, lazy, Component, useState, useEffect, useCallback, type ReactNode, type ErrorInfo } from 'react';
import {
  CalendarClock,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  X,
} from 'lucide-react';
import { useMatch, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { schedulerApiClient } from '@/lib/api';
import { useFacilitatorPortal, type Toast } from './useFacilitatorPortal';
import type { ScheduledSession } from '@/types/facilitator-portal';
import { guardedNavigate } from '@/lib/navGuard';
import { captureException } from '@/lib/telemetry';
import { importOrReload, isStaleBuildError } from '@/lib/staleBuild';
import { currentLocale, translate } from '@/lib/i18n';
import { liveStatusOf, proposalsWaitingFor, type FacilitatorPage, type Proposal } from '@/types/facilitator-portal';
import TopBar from './components/TopBar';
import StudioNotices, { NOTICES_ON } from './components/StudioNotices';
import NoticeBell from './components/NoticeBell';
import { useNoticeList } from './hooks/useNoticeList';

// ─── Error boundary (catches render crashes and shows details) ──────────────

interface EBProps { children: ReactNode }
interface EBState { error: Error | null }

class PortalErrorBoundary extends Component<EBProps, EBState> {
  state: EBState = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[FacilitatorPortal crash]', error, info.componentStack);
    captureException(error);
  }
  render() {
    if (this.state.error && isStaleBuildError(this.state.error)) {
      // A new version went out while this tab was open (see staleBuild.ts).
      const tr = (k: string) => translate(currentLocale(), k);
      return (
        <div className="flex min-h-screen items-center justify-center bg-[var(--fp-page,#0B0B0D)] p-8 text-[var(--fp-cream,#F4EFE6)]">
          <div className="max-w-md space-y-4 text-center">
            <h1 className="font-display text-2xl font-bold">{tr('stale.title')}</h1>
            <p className="text-[15px] opacity-75">{tr('stale.body')}</p>
            <button onClick={() => window.location.reload()} className="min-h-11 rounded-full bg-[var(--fp-cream)] px-6 text-sm font-bold text-[var(--fp-ink)]">
              {tr('stale.reload')}
            </button>
          </div>
        </div>
      );
    }
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-8">
          <div className="max-w-lg space-y-4">
            <h1 className="text-xl font-bold text-destructive">Something went wrong</h1>
            <pre className="text-sm bg-card border border-border rounded-lg p-4 overflow-auto whitespace-pre-wrap">
              {this.state.error.message}
              {'\n\n'}
              {this.state.error.stack}
            </pre>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Lazy sub-pages ──────────────────────────────────────────────────────────

const HomeTab          = lazy(importOrReload(() => import('./tabs/HomeTab')));   // Redesign screen 1, 1 Oct
const SessionsTab      = lazy(importOrReload(() => import('./tabs/SessionsTab')));
const WrapUpTab        = lazy(importOrReload(() => import('./tabs/WrapUpTab')));
const AvailabilityTab  = lazy(importOrReload(() => import('./tabs/AvailabilityTab')));
// ReviewTab + LocalResultsTab removed from FP nav 2026-06-12 (F6 — moved to CC).
// Files retained on disk for reference until CC port lands.
const ActiveDrillsTab  = lazy(importOrReload(() => import('./tabs/ActiveDrillsTab')));  // Nino 2026-05-20
const EarningsTab      = lazy(importOrReload(() => import('./tabs/EarningsTab')));     // Wave 2.6, 2026-06-12
const ProfileTab       = lazy(importOrReload(() => import('./tabs/ProfileTab')));
const AuthGate         = lazy(importOrReload(() => import('./tabs/AuthGate')));
const ObserverRoom     = lazy(importOrReload(() => import('./tabs/ObserverRoom')));

// ─── Nav config ──────────────────────────────────────────────────────────────



// ─── Toast overlay ───────────────────────────────────────────────────────────

function ToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}) {
  if (toasts.length === 0) return null;
  // 2026-09-29 UI audit redesign. Neutral raised surface, message in
  // normal text colour (readable), the type carried by a small coloured
  // icon, a close button on every toast, and a thin countdown line for
  // toasts that go away on their own. Top-centre on phones, top-right
  // from 640px. Errors still stay until dismissed (Scoring P1, 28 Sep).
  const icon: Record<Toast['type'], { Icon: typeof CheckCircle2; tone: string; bar: string }> = {
    success: { Icon: CheckCircle2, tone: 'text-positive bg-positive/15', bar: 'bg-positive' },
    error:   { Icon: AlertCircle,   tone: 'text-negative bg-negative/15', bar: 'bg-negative' },
    info:    { Icon: Info,          tone: 'text-foreground bg-foreground/10', bar: 'bg-foreground/60' },
    warning: { Icon: AlertTriangle, tone: 'text-warning bg-warning/15', bar: 'bg-warning' },
  };
  return (
    <div
      className="fp-toasts fixed z-[70] top-[4.75rem] inset-x-3 sm:inset-x-auto sm:right-5 sm:w-[380px] flex flex-col gap-2 pointer-events-none"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((t) => {
        const { Icon, tone, bar } = icon[t.type];
        return (
          <div
            key={t.id}
            role={t.type === 'error' || t.type === 'warning' ? 'alert' : 'status'}
            className="fp-toast-in pointer-events-auto relative overflow-hidden rounded-xl border border-foreground/10 bg-accent/95 backdrop-blur-md shadow-[0_16px_40px_-12px_rgba(0,0,0,0.75)] flex items-start gap-3 pl-3.5 pr-1.5 py-3"
          >
            <span className={cn('mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full', tone)}>
              <Icon className="h-3.5 w-3.5" aria-hidden />
            </span>
            <p className="flex-1 min-w-0 pt-0.5 text-sm leading-snug text-foreground">{t.msg}</p>
            <button
              type="button"
              onClick={() => onDismiss(t.id)}
              aria-label="Dismiss notification"
              className="shrink-0 -my-0.5 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
            {t.ttl != null && (
              <span
                aria-hidden
                className={cn('fp-toast-countdown absolute bottom-0 left-0 h-0.5 w-full origin-left opacity-60', bar)}
                style={{ animationDuration: `${t.ttl}ms` }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Page skeleton ───────────────────────────────────────────────────────────

function TabSkeleton() {
  return (
    <div className="p-6 space-y-4 animate-pulse">
      <div className="h-5 w-48 bg-muted rounded" />
      <div className="h-4 w-72 bg-muted/60 rounded" />
      <div className="h-40 bg-muted/30 rounded-xl mt-6" />
    </div>
  );
}

// ─── Main export ─────────────────────────────────────────────────────────────

export default function FacilitatorPortal() {
  const {
    facilitator,
    updateFacilitator,
    currentPage,
    setCurrentPage,
    toasts,
    toast,
    dismissToast,
    login,
    requestSignInCode,
    verifySignInCode,
    finishSignIn,
    logout,
  } = useFacilitatorPortal();
  // Pages with unsaved changes can ask before the move (lib/navGuard).
  const navigate = useCallback((page: FacilitatorPage) => guardedNavigate(() => setCurrentPage(page)), [setCurrentPage]);
  // The notification list behind the bell; refreshed every 30 s once signed in.
  const noticeList = useNoticeList(NOTICES_ON && !!facilitator);

  // The observer seat: /facilitator/observe/{bridge_session_id} (Scoring, 7 Oct).
  const observeMatch = useMatch('/observe/:sessionId');
  const routerNavigate = useNavigate();

  const [proposalBadgeCount, setProposalBadgeCount] = useState(0);
  const [hasLiveSession, setHasLiveSession] = useState(false);

  useEffect(() => {
    if (!facilitator) return;

    const loadData = async () => {
      // Badge: proposals waiting for this facilitator's answer only (it
      // counted every proposal of every status before, 2 Oct). Its own
      // try, so a proposals failure never hides the LIVE chip.
      try {
        const res = await schedulerApiClient.getProposals();
        const list: Proposal[] = Array.isArray(res) ? res : (res?.proposals ?? []);
        setProposalBadgeCount(proposalsWaitingFor(list, facilitator.facilitator_id).length);
      } catch {
        setProposalBadgeCount(0);
      }
      try {
        // Fetch sessions to check for live bridge session
        const sessions = await schedulerApiClient.listSessions({ facilitator_id: facilitator.facilitator_id });
        const hasLive = Array.isArray(sessions) && sessions.some((s: ScheduledSession) => liveStatusOf(s) === 'live');
        setHasLiveSession(hasLive);
      } catch (error) {
        console.error('[FacilitatorPortal] Error loading sidebar data:', error);
      }
    };

    loadData();
  }, [facilitator]);

  // ── Not logged in ────────────────────────────────────────────────────
  if (!facilitator) {
    // Standalone topbar removed 2026-06-27 — the redesigned AuthGate
    // already carries the Playbook brand lockup on the left panel and
    // the "Facilitator portal" caption above "Welcome back" on the
    // right form column. The extra topbar was redundant.
    return (
      <div className="min-h-screen bg-background text-foreground">
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
        <Suspense fallback={<TabSkeleton />}>
          <AuthGate
            onLogin={login}
            requestCode={requestSignInCode}
            verifyCode={verifySignInCode}
            finishSignIn={finishSignIn}
            toast={toast}
          />
        </Suspense>
      </div>
    );
  }

  // ── Observer seat (staff): before the approval gate, since the server,
  // not this account's facilitator status, decides who may observe. ──────
  if (observeMatch?.params.sessionId) {
    return (
      <PortalErrorBoundary>
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
        <Suspense fallback={<TabSkeleton />}>
          <ObserverRoom
            sessionId={observeMatch.params.sessionId}
            toast={toast}
            onExit={() => { routerNavigate('/'); setCurrentPage('dashboard'); }}
          />
        </Suspense>
      </PortalErrorBoundary>
    );
  }

  // ── Pending approval ────────────────────────────────────────────────
  if (facilitator.status === ('pending' as string)) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
        <div className="flex flex-col items-center justify-center py-24 text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/10 flex items-center justify-center">
            <CalendarClock className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-lg font-semibold">Pending Approval</h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            Your facilitator registration is being reviewed by an admin.
            You&apos;ll get access to all features once approved.
          </p>
          <button
            type="button"
            onClick={logout}
            className="text-sm text-muted-foreground hover:text-foreground underline mt-4"
          >
            Log out
          </button>
        </div>
      </div>
    );
  }

  // ── Main portal — full-screen standalone layout ─────────────────────
  // 2026-06-12 — h-screen (not min-h-screen) so the sidebar's h-full
  // resolves to viewport height, which is what actually pins the user
  // footer to the bottom-left the way OP's sidebar does.
  return (
    <PortalErrorBoundary>
      <div className="h-screen bg-background text-foreground flex">
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
        <StudioNotices onNavigate={navigate} onOpenList={() => noticeList.setOpen(true)} />

        <div className="flex-1 flex flex-col min-w-0">
          {/* Redesign screen 1 (Design, 1 Oct): a cream top bar with six
              destinations replaces the sidebar on every width. */}
          <TopBar
            currentPage={currentPage}
            onNavigate={navigate}
            displayName={facilitator.display_name || ''}
            hasLiveSession={hasLiveSession}
            proposalCount={proposalBadgeCount}
            bell={NOTICES_ON ? <NoticeBell list={noticeList} onNavigate={navigate} /> : undefined}
          />
          {/* Page content — full height, scrollable */}
          <div className="flex-1 overflow-y-auto">
            <Suspense fallback={<TabSkeleton />}>
              {currentPage === 'dashboard' && (
                <HomeTab facilitator={facilitator} onNavigate={navigate} onSignOut={logout} />
              )}
              {/* Redesign screen 4: one Sessions screen. "Prepare" (prep) opens its
                  list and "View week" (schedule) its week, from Home and the wrap-up. */}
              {(currentPage === 'sessions' || currentPage === 'prep' || currentPage === 'schedule') && (
                <SessionsTab
                  facilitator={facilitator}
                  onSignOut={logout}
                  toast={toast}
                  onNavigate={navigate}
                  initialView={currentPage === 'schedule' ? 'week' : 'list'}
                />
              )}
              {currentPage === 'wrapup' && (
                <WrapUpTab facilitator={facilitator} onNavigate={navigate} />
              )}
              
              {currentPage === 'availability' && (
                <AvailabilityTab facilitator={facilitator} toast={toast} onNavigate={navigate} />
              )}
              {/* Redesign screen 5: one Drills screen with Open drills and Proposals. */}
              {(currentPage === 'active-drills' || currentPage === 'proposals') && (
                <ActiveDrillsTab
                  facilitator={facilitator}
                  toast={toast}
                  onNavigate={navigate}
                  initialSection={currentPage === 'proposals' ? 'proposals' : 'open'}
                  proposalCount={proposalBadgeCount}
                />
              )}
              {currentPage === 'earnings' && (
                <EarningsTab facilitator={facilitator} toast={toast} />
              )}
              {currentPage === 'profile' && (
                <ProfileTab
                  facilitator={facilitator}
                  updateFacilitator={updateFacilitator}
                  onLogout={logout}
                  toast={toast}
                />
              )}
            </Suspense>
          </div>
        </div>
      </div>
    </PortalErrorBoundary>
  );
}
